import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useToast } from "@/components/ui/use-toast";
import {
  ASSESSMENT_OF_SYMPTOMS,
  useCareAssessments,
  useSaveReviewDraft,
  useSubmitReview,
  type AssessmentOfSymptoms,
  type CarePage,
  type CarePresentation,
  type SaveReviewDraftBody,
} from "@/lib/api/care";
import { PostHogEvents, trackEvent } from "@/lib/posthog-events";
import { Info, Pencil } from "lucide-react";
import { useState } from "react";
import { AssessmentResults } from "./assessment-results";
import {
  ASSESSMENT_MAX_SCORES,
  ASSESSMENT_NAMES,
  CareButton,
  CodeChip,
  FieldLabel,
  Pill,
  ScoreTag,
  formatCareDate,
} from "./care-primitives";
import { CareUpdateForm } from "./care-update-form";
import { FindPresentationDialog } from "./find-presentation-dialog";

const RATIONALE_HELP =
  "This rationale is generated from assessments completed within the current review window. When multiple assessments fall within that window, more recent assessments are weighted more heavily in the overall score.";

function filled(value: string | undefined) {
  const trimmed = value?.trim();
  return trimmed ? trimmed : undefined;
}

function assessmentOf(
  value: string | undefined,
): AssessmentOfSymptoms | undefined {
  return ASSESSMENT_OF_SYMPTOMS.find((option) => option === value?.trim());
}

export function toSaveReviewDraftBody(
  values: {
    assessment_of_symptoms?: AssessmentOfSymptoms | "";
    clinical_summary?: string;
    diagnostic_support_statement?: string;
  },
  presentation: CarePresentation | null,
): SaveReviewDraftBody {
  const assessment = assessmentOf(values.assessment_of_symptoms);
  const summary = filled(values.clinical_summary);
  const statement = filled(values.diagnostic_support_statement);
  return {
    assessment_of_symptoms: assessment ?? null,
    clinical_summary: summary ?? null,
    diagnostic_support_statement: statement ?? null,
    presentation,
  };
}

export function PresentationReviewCard({
  menteeId,
  page,
}: {
  menteeId: string;
  page: CarePage;
}) {
  const review = page.open_review;
  const [opened, setOpened] = useState(false);
  if (!review) return null;

  const onReview = () => {
    trackEvent(
      review.status === "draft"
        ? PostHogEvents.CARE_PAGE_REVIEW_DRAFT_RESUMED
        : PostHogEvents.CARE_PAGE_REVIEW_PRESENTATION_REVIEW_CLICKED,
      { mentee_id: menteeId, review_type: review.review_type },
    );
    setOpened(true);
  };

  return (
    <section className="overflow-hidden rounded-[20px] border border-[rgba(31,30,29,0.16)] bg-white shadow-[0px_9px_31.2px_rgba(0,0,0,0.13)]">
      <header className="flex h-14 items-center justify-between border-b border-[rgba(94,74,33,0.22)] bg-[#ebe0cc] px-6 py-2">
        <span className="inline-flex items-center gap-2">
          {!opened ? (
            <span className="size-2 rounded-full bg-[#f93b51]" aria-hidden />
          ) : null}
          <Pill tone="ivoryLight">Presentation review</Pill>
        </span>
        <span className="inline-flex items-center gap-4">
          {review.status === "draft" ? (
            <span className="font-body text-xs leading-4 text-[#735f31]">
              Draft
            </span>
          ) : null}
          <CareButton variant="filled" onClick={onReview} disabled={opened}>
            Review
          </CareButton>
        </span>
      </header>
      <PresentationReviewBody
        menteeId={menteeId}
        page={page}
        expanded={opened}
        onDraftSaved={() => setOpened(false)}
        onSubmitted={() => setOpened(false)}
      />
    </section>
  );
}

export function PresentationReviewBody({
  menteeId,
  page,
  expanded,
  onSubmitted,
  onDraftSaved,
}: {
  menteeId: string;
  page: CarePage;
  expanded: boolean;
  onSubmitted?: () => void;
  onDraftSaved?: () => void;
}) {
  const review = page.open_review;
  const { toast } = useToast();
  const [finding, setFinding] = useState(false);
  const [chosen, setChosen] = useState<CarePresentation | null>(null);
  const saveDraft = useSaveReviewDraft(menteeId);
  const submit = useSubmitReview(menteeId);

  if (!review) return null;

  const selected = chosen ?? review.presentation ?? review.recommended;
  const overridden = selected.code !== review.recommended.code;
  const isNew = selected.code !== page.presentation?.code;
  const pending = saveDraft.isPending || submit.isPending;

  return (
    <>
      <div className="flex flex-col gap-4 px-6 py-4">
        <div className="flex items-start justify-between gap-4">
          <div className="flex flex-col gap-1">
            <FieldLabel className="inline-flex items-center gap-1">
              Recommended presentation
              <HelpTip text={RATIONALE_HELP} />
            </FieldLabel>
            <div className="flex flex-wrap items-center gap-2">
              <CodeChip code={selected.code} size="sm" />
              <p className="font-heading text-xl font-bold leading-6 text-[#1f1e1d]">
                {selected.description}
              </p>
              {isNew ? <Pill tone="sky">New presentation</Pill> : null}
            </div>
          </div>
          {expanded ? (
            <CareButton
              className="min-w-0 shrink-0"
              onClick={() => {
                trackEvent(
                  PostHogEvents.CARE_PAGE_REVIEW_CHANGE_PRESENTATION_CLICKED,
                  { mentee_id: menteeId },
                );
                setFinding(true);
              }}
            >
              <Pencil className="size-3.5" aria-hidden />
              Change presentation
            </CareButton>
          ) : null}
        </div>

        <div className="flex flex-col gap-1 border-t border-[#dfddd8] pt-4">
          <FieldLabel>Rationale</FieldLabel>
          <p className="font-body text-base leading-6 text-[#1f1e1d]">
            {review.rationale ?? "—"}
          </p>
        </div>

        <div className="flex flex-col gap-2">
          <FieldLabel>Assessments ({review.assessment_ids.length})</FieldLabel>
          <AssessmentSummary
            menteeId={menteeId}
            ids={review.assessment_ids}
            page={page}
          />
          <AssessmentResults menteeId={menteeId} ids={review.assessment_ids} />
        </div>

        {expanded ? (
          <div className="border-t border-[#dfddd8] pt-6">
            <CareUpdateForm
              key={review.id}
              title="Care Update"
              menteeId={menteeId}
              presentation={selected}
              pending={pending}
              pendingAction={saveDraft.isPending ? "secondary" : "submit"}
              defaultValues={{
                assessment_of_symptoms:
                  (review.assessment_of_symptoms as AssessmentOfSymptoms | null) ??
                  undefined,
                clinical_summary: review.clinical_summary ?? undefined,
                diagnostic_support_statement:
                  review.diagnostic_support_statement ?? undefined,
              }}
              secondaryAction={{
                label: "Save draft",
                onClick: (values) => {
                  trackEvent(
                    PostHogEvents.CARE_PAGE_REVIEW_SAVE_DRAFT_CLICKED,
                    {
                      mentee_id: menteeId,
                    },
                  );
                  saveDraft.mutate(
                    toSaveReviewDraftBody(values, overridden ? selected : null),
                    {
                      onSuccess: () => onDraftSaved?.(),
                      onError: () =>
                        toast({
                          title: "Could not save draft",
                          variant: "destructive",
                        }),
                    },
                  );
                },
              }}
              onSubmit={(values) =>
                submit.mutate(
                  {
                    ...values,
                    presentation: overridden ? selected : undefined,
                  },
                  {
                    onSuccess: () => {
                      trackEvent(PostHogEvents.CARE_PAGE_REVIEW_SUBMITTED, {
                        mentee_id: menteeId,
                        review_type: review.review_type,
                        presentation_changed: overridden,
                      });
                      setChosen(null);
                      onSubmitted?.();
                    },
                    onError: () =>
                      toast({
                        title: "Could not submit review",
                        variant: "destructive",
                      }),
                  },
                )
              }
            />
          </div>
        ) : null}
      </div>

      <FindPresentationDialog
        open={finding}
        current={page.presentation}
        onClose={() => setFinding(false)}
        onSelect={(presentation) => {
          setChosen(presentation);
          setFinding(false);
        }}
      />
    </>
  );
}

function AssessmentSummary({
  menteeId,
  ids,
  page,
}: {
  menteeId: string;
  ids: string[];
  page: CarePage;
}) {
  const assessments = useCareAssessments(menteeId, ids, ids.length > 0);
  const fromReview = assessments.data?.at(-1);
  const score = fromReview
    ? {
        score: fromReview.score,
        severity: fromReview.severity,
        taken_at: fromReview.taken_at,
      }
    : page.latest_score;
  const name = ASSESSMENT_NAMES[fromReview?.assessment ?? page.assessment];
  return (
    <div className="grid grid-cols-[1.2fr_1.4fr_1fr] gap-4 rounded-xl bg-[#f6f4ef] px-6 py-3">
      <div className="flex flex-col gap-1">
        <FieldLabel className="text-[#b5b0a6]">Assigned assessment</FieldLabel>
        <p className="font-heading text-xl font-bold leading-6 text-[#1f1e1d]">
          {name}
        </p>
      </div>
      <div className="flex flex-col gap-1">
        <FieldLabel className="text-[#b5b0a6]">Score</FieldLabel>
        {score ? (
          <ScoreTag
            score={score.score}
            severity={score.severity}
            max={
              ASSESSMENT_MAX_SCORES[fromReview?.assessment ?? page.assessment]
            }
            size="sm"
          />
        ) : (
          <p className="font-body text-base text-[#58534b]">—</p>
        )}
      </div>
      <div className="flex flex-col gap-1">
        <FieldLabel className="text-[#b5b0a6]">Assessment</FieldLabel>
        <p className="font-mono text-[10px] leading-3 text-[#6e6b67]">
          {score ? formatCareDate(score.taken_at) : "—"}
        </p>
      </div>
    </div>
  );
}

export function HelpTip({ text }: { text: string }) {
  return (
    <TooltipProvider delayDuration={150}>
      <Tooltip>
        <TooltipTrigger asChild>
          <button type="button" aria-label="More info" className="inline-flex">
            <Info className="size-4 text-text-secondary" aria-hidden />
          </button>
        </TooltipTrigger>
        <TooltipContent className="max-w-xs font-body text-xs leading-4">
          {text}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
